import { EQUIPMENT_TYPES } from './constants'

export const normalizeMac = (mac) =>
  String(mac || '').toLowerCase().replace(/[^a-f0-9]/g, '')

export const getEquipmentInfo = (type) =>
  EQUIPMENT_TYPES.find(eq => eq.value === type) || EQUIPMENT_TYPES.find(eq => eq.value === 'other')

export const getNodeStatus = (isReachable) => {
  if (isReachable === true) return 'up'
  if (isReachable === false) return 'down'
  return 'missing_ref'
}

export const getStatusColor = (status) => {
  const map = { up: '#1abc9c', down: '#ef4444', missing_ref: '#9ca3af' }
  return map[status] || '#9ca3af'
}

export const getLinkSpeed = (speed) => {
  const map = { 10000000000: '10gb', 1000000000: '1gb', 100000000: '100mb', 10000000: '10mb' }
  return map[speed] || 'unknow'
}

export const formatTopologyPortTooltip = (port, parentName) => {
  const normalizedPort = String(port ?? '').trim()
  if (!normalizedPort) return ''
  if (!parentName) return normalizedPort

  const match = normalizedPort.match(/(?:Ten)?GigabitEthernet(.+)$/i)
    || normalizedPort.match(/Port\s+(.+)$/i)
  const portNumber = match?.[1] || normalizedPort.split('/').filter(Boolean).pop() || normalizedPort
  return `${normalizedPort} \u2192 ${parentName}${portNumber ? ` Port ${portNumber}` : ''}`
}

export const buildTopologyParentTree = (network, targetMac, maxDepth = 4) => {
  const normalizedTargetMac = normalizeMac(targetMac)
  if (!normalizedTargetMac || !network?.nodes?.length) return null

  const nodeByMac = Object.fromEntries(
    network.nodes.map(node => [normalizeMac(node.mac_address || node.mac), node])
  )
  const buildChain = (node, depth = 0, seen = new Set()) => {
    if (!node || depth >= maxDepth) return []

    const mac = normalizeMac(node.mac_address || node.mac)
    if (!mac || seen.has(mac)) return []

    const parent = nodeByMac[normalizeMac(node.parent_mac)]
    const nextSeen = new Set(seen).add(mac)
    return [...buildChain(parent, depth + 1, nextSeen), node]
  }

  const chain = buildChain(nodeByMac[normalizedTargetMac])

  return chain.reduceRight((child, node) => ({
    ...node,
    linkSpeed: node.link_speed ?? node.speed ?? null,
    parentPort: node.parent_port ?? null,
    parentName: nodeByMac[normalizeMac(node.parent_mac)]?.name || node.parent_name || null,
    children: child ? [child] : []
  }), null)
}

export const getModelName = (model) => {
  if (!model) return null
  if (typeof model === 'string') return model
  if (typeof model === 'object') return Object.values(model).find(v => typeof v === 'string' && v.length > 0) || null
  return null
}

export const getLocationFlags = (device, parentDevice, mainSwitch) => {
  const ref = parentDevice || mainSwitch
  const hasPosition = Boolean(device.building_name || device.floor_name)
  const refHasPosition = Boolean(ref?.building_name || ref?.floor_name)

  if (!hasPosition && !device.is_main_switch) {
    if (refHasPosition) return { positionless: true, isSameBuilding: null, isSameFloor: null }
    return { positionless: false, isSameBuilding: null, isSameFloor: null }
  }
  if (!refHasPosition || !hasPosition) {
    return { positionless: false, isSameBuilding: null, isSameFloor: null }
  }

  const isSameBuilding = ref.building_name === device.building_name
  const isSameFloor = isSameBuilding && ref.floor_name === device.floor_name
  return { positionless: false, isSameBuilding, isSameFloor }
}

const buildConnectionMaps = (connections) => {
  const childrenMap = {}
  const parentMap = {}
  connections.forEach(conn => {
    if (!childrenMap[conn.parent_mac]) childrenMap[conn.parent_mac] = []
    childrenMap[conn.parent_mac].push({ mac: conn.device_mac, speed: conn.speed, port: conn.port })
    parentMap[conn.device_mac] = conn.parent_mac
  })
  return { childrenMap, parentMap }
}

const buildTreeNode = (device, linkSpeed, parentDevice, { nodeByMac, childrenMap, seen, mainSwitch, allChildrenMap, allNodeMacs }) => {
  if (seen.has(device.mac_address)) return null
  seen.add(device.mac_address)

  const { positionless, isSameBuilding, isSameFloor } = getLocationFlags(device, parentDevice, mainSwitch)

  const children = (childrenMap[device.mac_address] || [])
    .map(c => {
      const childDevice = nodeByMac[c.mac]
      return childDevice
        ? buildTreeNode(childDevice, c.speed, device, { nodeByMac, childrenMap, seen, mainSwitch, allChildrenMap, allNodeMacs })
        : null
    })
    .filter(Boolean)

  const totalChildren = (allChildrenMap[device.mac_address] || [])
    .filter(c => allNodeMacs.has(c.mac) && !nodeByMac[c.mac])
    .length

  return {
    name: device.name,
    mac_address: device.mac_address,
    type: device.type,
    ip_address: device.ip_address,
    serial: device.serial,
    model: device.model,
    is_reachable: device.is_reachable,
    is_main_switch: device.is_main_switch,
    root: device.root,
    building_name: device.building_name,
    floor_name: device.floor_name,
    linkSpeed,
    positionless,
    isSameBuilding,
    isSameFloor,
    totalChildren,
    children,
  }
}

const collectOrphans = (nodes, seen, mainSwitch) =>
  nodes
    .filter(n => !seen.has(n.mac_address))
    .map(n => ({
      ...n,
      isOrphan: true,
      linkSpeed: null,
      children: [],
      ...getLocationFlags(n, null, mainSwitch),
    }))

export const transformTopologyToTree = (network, hiddenTypes = new Set(), revealedParentMacs = new Set()) => {
  if (!network?.nodes?.length) return null

  const { nodes: allNodes, connections: allConnections } = network
  const { parentMap: allParentMap, childrenMap: allChildrenMap } = buildConnectionMaps(allConnections)
  const nodes = allNodes.filter(n =>
    !hiddenTypes.has(n.type || 'other') || revealedParentMacs.has(allParentMap[n.mac_address])
  )
  const visibleMacs = new Set(nodes.map(n => n.mac_address))
  const connections = allConnections.filter(c => visibleMacs.has(c.parent_mac))

  const { childrenMap, parentMap } = buildConnectionMaps(connections)
  const nodeByMac = Object.fromEntries(nodes.map(n => [n.mac_address, n]))
  const allNodeMacs = new Set(allNodes.map(n => n.mac_address))
  const mainSwitch = nodes.find(n => n.is_main_switch) || nodes.find(n => n.root === 0)
  const seen = new Set()
  const ctx = { nodeByMac, childrenMap, seen, mainSwitch, allChildrenMap, allNodeMacs }

  const roots = nodes
    .filter(n => n.root === 0 || !parentMap[n.mac_address])
    .map(n => buildTreeNode(n, null, null, ctx))
    .filter(Boolean)

  const orphans = collectOrphans(nodes, seen, mainSwitch)
  const allChildren = [...roots, ...orphans]

  if (allChildren.length === 1) return allChildren[0]
  return { name: 'Root', isVirtualRoot: true, children: allChildren }
}

export const flattenTopologyEquipments = (network) => {
  if (!network?.nodes) return []
  return network.nodes.map(n => ({
    name: n.name,
    mac_address: n.mac_address,
    type: n.type,
    ip_address: n.ip_address,
    serial: n.serial,
    model: n.model,
    is_reachable: n.is_reachable,
    manufacturer: n.manufacturer,
    building_name: n.building_name,
    floor_name: n.floor_name,
    role: n.role,
    is_main_switch: n.is_main_switch,
    parent_mac: n.parent_mac || null,
    parent_name: n.parent_name || null,
    parent_port: n.parent_port ?? null,
  }))
}
